import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Avatar,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText
} from '@mui/material';
import {
  Cake as BirthdayIcon,
  Favorite as AnniversaryIcon,
  ChevronLeft,
  ChevronRight,
  Today as TodayIcon,
  CalendarMonth as MonthIcon,
  CalendarViewMonth as YearIcon,
  Close as CloseIcon,
  Event as CalendarEventIcon,
} from '@mui/icons-material';
import { useProfile } from '../contexts/ProfileContext';
import { useNavigate } from 'react-router-dom';
import { getUpcomingBirthdays } from '../services/dashboardService';
import { db } from '../firebase';
import { collection, query, where, orderBy, getDocs } from 'firebase/firestore';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths, getDay, startOfYear, addYears, subYears } from 'date-fns';
import logger from '../utils/logger';

// Convert "HH:MM" (24h) → "h:MM AM/PM"
const formatTime12h = (t) => {
  if (!t) return '';
  const [hStr, mStr] = t.split(':');
  const h = parseInt(hStr, 10);
  const m = mStr || '00';
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${h12}:${m} ${period}`;
};

function BirthdayCalendarPage() {
  const { profile } = useProfile();
  const navigate = useNavigate();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [birthdays, setBirthdays] = useState([]);
  const [familyEvents, setFamilyEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('month'); // 'month' or 'year'
  const [selectedDay, setSelectedDay] = useState(null);
  const [dayDialogOpen, setDayDialogOpen] = useState(false);

  useEffect(() => {
    const fetchAll = async () => {
      if (!profile?.familyCode) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        // Birthday/anniversary data (365 days ahead)
        const birthdayData = await getUpcomingBirthdays(profile.familyCode, 365);
        setBirthdays(birthdayData);

        // Family events — fetch a full year window
        const todayStr = new Date().toISOString().split('T')[0];
        const oneYearStr = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
        const eventsSnap = await getDocs(
          query(
            collection(db, 'families', profile.familyCode, 'events'),
            where('date', '>=', todayStr),
            where('date', '<=', oneYearStr),
            orderBy('date', 'asc'),
          )
        );
        const events = eventsSnap.docs.map(doc => {
          const d = doc.data();
          // Parse "YYYY-MM-DD" as local date to avoid UTC midnight shift
          const [y, m, day] = (d.date || '').split('-').map(Number);
          const localDate = (y && m && day) ? new Date(y, m - 1, day) : null;
          return {
            id: doc.id,
            type: 'event',
            name: d.title || 'Family Event',
            date: localDate,
            time: d.time || '',
            location: d.location?.address || (typeof d.location === 'string' ? d.location : ''),
            eventType: d.eventType || 'casual',
          };
        }).filter(e => e.date);
        setFamilyEvents(events);

        setError(null);
      } catch (err) {
        logger.error('Error fetching calendar data:', err);
        setError('Failed to load calendar data');
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, [profile?.familyCode]);

  const handlePrevMonth = () => {
    setCurrentMonth(subMonths(currentMonth, 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(addMonths(currentMonth, 1));
  };

  const handleToday = () => {
    setCurrentMonth(new Date());
  };

  const handleViewModeChange = (event, newMode) => {
    if (newMode !== null) {
      setViewMode(newMode);
    }
  };

  const handleDayClick = (day, events) => {
    if (events.length > 0) {
      setSelectedDay({ date: day, events });
      setDayDialogOpen(true);
    }
  };

  const handleCloseDayDialog = () => {
    setDayDialogOpen(false);
    setSelectedDay(null);
  };

  const handlePrevYear = () => {
    setCurrentMonth(subYears(currentMonth, 1));
  };

  const handleNextYear = () => {
    setCurrentMonth(addYears(currentMonth, 1));
  };

  // Get days for current month view
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });

  // Get starting day offset (0 = Sunday)
  const startDayOffset = getDay(monthStart);

  // Merge all calendar items (birthdays, anniversaries, family events)
  const allCalendarItems = [...birthdays, ...familyEvents];

  // Get events for a specific day
  const getEventsForDay = (day) => {
    return allCalendarItems.filter(event => isSameDay(event.date, day));
  };

  // Get all events for the current month (for the list view)
  const eventsThisMonth = allCalendarItems.filter(event =>
    isSameMonth(event.date, currentMonth)
  );

  // Separate upcoming events into categories
  const upcomingBirthdays = birthdays.filter(e => e.type === 'birthday').slice(0, 10);
  const upcomingAnniversaries = birthdays.filter(e => e.type === 'anniversary').slice(0, 10);
  const upcomingFamilyEvents = familyEvents.slice(0, 10);

  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Get events for a specific month (for year view)
  const getEventsForMonth = (monthDate) => {
    return allCalendarItems.filter(event => isSameMonth(event.date, monthDate));
  };

  // Generate months for year view
  const yearStart = startOfYear(currentMonth);
  const monthsInYear = Array.from({ length: 12 }, (_, i) => addMonths(yearStart, i));

  if (!profile?.familyCode) {
    return (
      <Box p={3}>
        <Alert severity="info">Please join a family to view the birthday calendar.</Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <BirthdayIcon color="primary" />
        Family Calendar
      </Typography>

      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <Grid container spacing={3}>
          {/* Calendar View */}
          <Grid item xs={12} lg={8}>
            <Paper sx={{ p: 2 }}>
              {/* Calendar Header */}
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
                <Box display="flex" alignItems="center" gap={1}>
                  <IconButton onClick={viewMode === 'month' ? handlePrevMonth : handlePrevYear} size="small">
                    <ChevronLeft />
                  </IconButton>
                  <Typography variant="h5" sx={{ minWidth: 200, textAlign: 'center' }}>
                    {viewMode === 'month' ? format(currentMonth, 'MMMM yyyy') : format(currentMonth, 'yyyy')}
                  </Typography>
                  <IconButton onClick={viewMode === 'month' ? handleNextMonth : handleNextYear} size="small">
                    <ChevronRight />
                  </IconButton>
                </Box>
                <Box display="flex" alignItems="center" gap={1}>
                  <ToggleButtonGroup
                    value={viewMode}
                    exclusive
                    onChange={handleViewModeChange}
                    size="small"
                  >
                    <ToggleButton value="month" title="Month view">
                      <MonthIcon sx={{ mr: 0.5 }} /> Month
                    </ToggleButton>
                    <ToggleButton value="year" title="Year view">
                      <YearIcon sx={{ mr: 0.5 }} /> Year
                    </ToggleButton>
                  </ToggleButtonGroup>
                  <Tooltip title="Go to today">
                    <IconButton onClick={handleToday} color="primary">
                      <TodayIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              {viewMode === 'month' ? (
                <>
                  {/* Weekday Headers */}
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0 }}>
                    {weekDays.map(day => (
                      <Box key={day} sx={{ textAlign: 'center', py: 1, bgcolor: 'grey.100', borderBottom: '2px solid', borderColor: 'primary.main' }}>
                        <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
                          {day}
                        </Typography>
                      </Box>
                    ))}
                  </Box>

                  {/* Calendar Days - Fixed 7-column grid */}
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0, minHeight: 480 }}>
                    {/* Empty cells for offset */}
                    {Array.from({ length: startDayOffset }).map((_, index) => (
                      <Box key={`empty-${index}`} sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        minHeight: 80,
                        bgcolor: 'action.hover'
                      }} />
                    ))}

                    {/* Actual days */}
                    {daysInMonth.map(day => {
                      const dayEvents = getEventsForDay(day);
                      const isToday = isSameDay(day, new Date());
                      const hasEvents = dayEvents.length > 0;

                      return (
                        <Box
                          key={day.toISOString()}
                          onClick={() => handleDayClick(day, dayEvents)}
                          sx={{
                            border: '1px solid',
                            borderColor: isToday ? 'primary.main' : 'divider',
                            borderWidth: isToday ? 2 : 1,
                            minHeight: 80,
                            p: 0.5,
                            bgcolor: isToday ? 'primary.lighter' : 'background.paper',
                            position: 'relative',
                            cursor: hasEvents ? 'pointer' : 'default',
                            transition: 'all 0.2s',
                            '&:hover': hasEvents ? {
                              bgcolor: 'action.hover',
                              transform: 'scale(1.02)',
                              zIndex: 1,
                              boxShadow: 2
                            } : {}
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: isToday ? 700 : 400,
                              color: isToday ? 'primary.main' : 'text.primary',
                              mb: 0.5,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between'
                            }}
                          >
                            {format(day, 'd')}
                            {dayEvents.length > 2 && (
                              <Chip
                                size="small"
                                label={`+${dayEvents.length - 2}`}
                                sx={{ height: 16, fontSize: 10 }}
                              />
                            )}
                          </Typography>

                          {dayEvents.slice(0, 2).map(event => {
                            const isEvent = event.type === 'event';
                            const isBirthday = event.type === 'birthday';
                            const tooltipTitle = isEvent
                              ? `${event.name}${event.time ? ` · ${formatTime12h(event.time)}` : ''}`
                              : isBirthday
                                ? `${event.name}'s ${event.age}${getOrdinalSuffix(event.age)} Birthday`
                                : `${event.name}'s ${event.years}${getOrdinalSuffix(event.years)} Anniversary`;
                            const chipIcon = isEvent
                              ? <CalendarEventIcon sx={{ fontSize: 12 }} />
                              : isBirthday
                                ? <BirthdayIcon sx={{ fontSize: 12 }} />
                                : <AnniversaryIcon sx={{ fontSize: 12 }} />;
                            const chipBg = isEvent ? 'primary.lighter' : isBirthday ? 'success.lighter' : 'error.lighter';
                            const chipColor = isEvent ? 'primary.dark' : isBirthday ? 'success.dark' : 'error.dark';
                            return (
                              <Tooltip key={event.id} title={tooltipTitle}>
                                <Chip
                                  size="small"
                                  icon={chipIcon}
                                  label={event.name.split(' ')[0]}
                                  sx={{
                                    height: 18,
                                    fontSize: 10,
                                    mb: 0.25,
                                    width: '100%',
                                    justifyContent: 'flex-start',
                                    bgcolor: chipBg,
                                    color: chipColor,
                                    '& .MuiChip-icon': { color: 'inherit' }
                                  }}
                                />
                              </Tooltip>
                            );
                          })}
                        </Box>
                      );
                    })}
                  </Box>
                </>
              ) : (
                /* Year View - 12 month cards */
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(4, 1fr)' }, gap: 2 }}>
                  {monthsInYear.map(month => {
                    const monthEvents = getEventsForMonth(month);
                    const birthdayCount = monthEvents.filter(e => e.type === 'birthday').length;
                    const anniversaryCount = monthEvents.filter(e => e.type === 'anniversary').length;
                    const eventCount = monthEvents.filter(e => e.type === 'event').length;
                    const isCurrentMonth = isSameMonth(month, new Date());

                    return (
                      <Card
                        key={month.toISOString()}
                        onClick={() => {
                          setCurrentMonth(month);
                          setViewMode('month');
                        }}
                        sx={{
                          cursor: 'pointer',
                          border: isCurrentMonth ? '2px solid' : '1px solid',
                          borderColor: isCurrentMonth ? 'primary.main' : 'divider',
                          transition: 'all 0.2s',
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: 3
                          }
                        }}
                      >
                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                          <Typography variant="h6" fontWeight={600} color={isCurrentMonth ? 'primary.main' : 'text.primary'}>
                            {format(month, 'MMMM')}
                          </Typography>
                          <Box display="flex" gap={1} mt={1}>
                            {birthdayCount > 0 && (
                              <Chip
                                size="small"
                                icon={<BirthdayIcon sx={{ fontSize: 14 }} />}
                                label={birthdayCount}
                                sx={{ bgcolor: 'success.lighter', color: 'success.dark' }}
                              />
                            )}
                            {anniversaryCount > 0 && (
                              <Chip
                                size="small"
                                icon={<AnniversaryIcon sx={{ fontSize: 14 }} />}
                                label={anniversaryCount}
                                sx={{ bgcolor: 'error.lighter', color: 'error.dark' }}
                              />
                            )}
                            {eventCount > 0 && (
                              <Chip
                                size="small"
                                icon={<CalendarEventIcon sx={{ fontSize: 14 }} />}
                                label={eventCount}
                                sx={{ bgcolor: 'primary.lighter', color: 'primary.dark' }}
                              />
                            )}
                            {monthEvents.length === 0 && (
                              <Typography variant="caption" color="text.secondary">
                                No events
                              </Typography>
                            )}
                          </Box>
                        </CardContent>
                      </Card>
                    );
                  })}
                </Box>
              )}

              {/* Legend */}
              <Box display="flex" gap={2} mt={2} justifyContent="center" flexWrap="wrap">
                <Chip
                  size="small"
                  icon={<BirthdayIcon />}
                  label="Birthday"
                  sx={{ bgcolor: 'success.lighter', color: 'success.dark' }}
                />
                <Chip
                  size="small"
                  icon={<AnniversaryIcon />}
                  label="Anniversary"
                  sx={{ bgcolor: 'error.lighter', color: 'error.dark' }}
                />
                <Chip
                  size="small"
                  icon={<CalendarEventIcon />}
                  label="Event"
                  sx={{ bgcolor: 'primary.lighter', color: 'primary.dark' }}
                />
              </Box>
            </Paper>
          </Grid>

          {/* Sidebar - Upcoming Events */}
          <Grid item xs={12} lg={4}>
            {/* This Month */}
            {eventsThisMonth.length > 0 && (
              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TodayIcon color="primary" fontSize="small" />
                    This Month
                  </Typography>
                  {eventsThisMonth.map(event => (
                    <EventListItem key={event.id} event={event} />
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Upcoming Birthdays */}
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <BirthdayIcon color="success" fontSize="small" />
                  Upcoming Birthdays
                </Typography>
                {upcomingBirthdays.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No upcoming birthdays. Add birthdays in member profiles!
                  </Typography>
                ) : (
                  upcomingBirthdays.map(event => (
                    <EventListItem key={event.id} event={event} />
                  ))
                )}
              </CardContent>
            </Card>

            {/* Upcoming Anniversaries */}
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AnniversaryIcon color="error" fontSize="small" />
                  Upcoming Anniversaries
                </Typography>
                {upcomingAnniversaries.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No upcoming anniversaries. Add anniversary dates in profiles!
                  </Typography>
                ) : (
                  upcomingAnniversaries.map(event => (
                    <EventListItem key={event.id} event={event} />
                  ))
                )}
              </CardContent>
            </Card>

            {/* Upcoming Family Events */}
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CalendarEventIcon color="primary" fontSize="small" />
                  Upcoming Events
                </Typography>
                {upcomingFamilyEvents.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No upcoming events. Create events on the Events page!
                  </Typography>
                ) : (
                  upcomingFamilyEvents.map(event => (
                    <Box key={event.id} sx={{ mb: 1.5 }}>
                      <Typography variant="body2" fontWeight={600}>{event.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {format(event.date, 'MMM d')}{event.time ? ` · ${formatTime12h(event.time)}` : ''}
                        {event.location ? ` · ${event.location}` : ''}
                      </Typography>
                    </Box>
                  ))
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Day Details Dialog */}
      <Dialog open={dayDialogOpen} onClose={handleCloseDayDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box display="flex" alignItems="center" gap={1}>
            <BirthdayIcon color="primary" />
            <Typography variant="h6">
              {selectedDay && format(selectedDay.date, 'MMMM d, yyyy')}
            </Typography>
          </Box>
          <IconButton onClick={handleCloseDayDialog} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedDay && selectedDay.events.length > 0 ? (
            <List>
              {selectedDay.events.map(event => (
                <ListItem
                  key={event.id}
                  sx={{
                    px: 0, borderRadius: 1,
                    cursor: event.type === 'event' ? 'pointer' : 'default',
                    '&:hover': event.type === 'event' ? { bgcolor: 'action.hover' } : {}
                  }}
                  onClick={event.type === 'event' ? () => { handleCloseDayDialog(); navigate('/events'); } : undefined}
                >
                  <ListItemAvatar>
                    <Avatar
                      src={event.photoUrl}
                      sx={{
                        bgcolor: event.type === 'event' ? 'primary.main' : event.type === 'birthday' ? 'success.main' : 'error.main'
                      }}
                    >
                      {event.type === 'event' ? <CalendarEventIcon /> : event.name.charAt(0)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={event.name}
                    secondary={
                      event.type === 'event'
                        ? `${event.time ? formatTime12h(event.time) + (event.location ? ' · ' : '') : ''}${event.location || ''}`
                        : event.type === 'birthday'
                          ? `${event.age}${getOrdinalSuffix(event.age)} Birthday`
                          : `${event.years}${getOrdinalSuffix(event.years)} Anniversary`
                    }
                  />
                  <Chip
                    size="small"
                    icon={event.type === 'event' ? <CalendarEventIcon /> : event.type === 'birthday' ? <BirthdayIcon /> : <AnniversaryIcon />}
                    label={event.type === 'event' ? 'Event' : event.type === 'birthday' ? 'Birthday' : 'Anniversary'}
                    sx={{
                      bgcolor: event.type === 'event' ? 'primary.lighter' : event.type === 'birthday' ? 'success.lighter' : 'error.lighter',
                      color: event.type === 'event' ? 'primary.dark' : event.type === 'birthday' ? 'success.dark' : 'error.dark'
                    }}
                  />
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography color="text.secondary">No events on this day.</Typography>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}

// Helper component for event list items
function EventListItem({ event }) {
  const isEvent = event.type === 'event';
  const avatarColor = isEvent ? 'primary.main' : event.type === 'birthday' ? 'success.main' : 'error.main';

  const subtitle = isEvent
    ? `${format(event.date, 'MMM d')}${event.time ? ' · ' + formatTime12h(event.time) : ''}${event.location ? ' · ' + event.location : ''}`
    : `${format(event.date, 'MMM d')} · ${event.type === 'birthday' ? `Turning ${event.age}` : `${event.years} years`}`;

  return (
    <Box
      display="flex"
      alignItems="center"
      gap={1.5}
      py={1}
      sx={{ borderBottom: '1px solid', borderColor: 'divider', '&:last-child': { borderBottom: 'none' } }}
    >
      <Avatar src={event.photoUrl} sx={{ width: 36, height: 36, bgcolor: avatarColor }}>
        {isEvent ? <CalendarEventIcon fontSize="small" /> : event.name.charAt(0)}
      </Avatar>
      <Box flex={1}>
        <Typography variant="body2" fontWeight={500}>{event.name}</Typography>
        <Typography variant="caption" color="text.secondary">{subtitle}</Typography>
      </Box>
      {!isEvent && (
        <Chip
          size="small"
          label={event.isToday ? 'Today!' : event.daysUntil === 1 ? 'Tomorrow' : `${event.daysUntil} days`}
          color={event.isToday ? 'success' : event.daysUntil <= 7 ? 'warning' : 'default'}
          variant={event.isToday ? 'filled' : 'outlined'}
          sx={{ fontSize: 11 }}
        />
      )}
    </Box>
  );
}

// Helper function for ordinal suffix
function getOrdinalSuffix(n) {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return s[(v - 20) % 10] || s[v] || s[0];
}

export default BirthdayCalendarPage;
